import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RoleScope } from '../api/models/user.model';
import { useGetCurrentUserQuery } from '../api/user.api';
import { selectAccount } from '../redux/slices/accountSlice';

export function useUserAccountRole() {
	// Select the full selected account from Redux
	const selectedAccount = useSelector(selectAccount);

	// Get user data from RTK Query
	const { data: user, isLoading, error } = useGetCurrentUserQuery();

	// Memoize the role lookup for the selected account
	const role = useMemo(() => {
		if (!user?.roles || !selectedAccount?.accountId) return null;
    // Internal users will only ever have 1 role that isn't associated with an account so use it
		if (user?.roles?.length === 1 && user.roles[0].roleType === 'INTERNAL_USER') {
			return user.roles[0];
		}
    // else find the matching role based on the select account ID
		return user.roles.find((r) => r.accountId === selectedAccount.accountId) || null;
	}, [user, selectedAccount]);

	return { role, isLoading, error };
}

/**
 * Checks if the role for the selected user account has the supplied roleScope.
 * @returns `false` if either `role` or `roleScope` are undefined  
 */
export function useUserAccountRoleHasRoleScope(roleScope?: RoleScope) {
	const { role } = useUserAccountRole();
	return Boolean(roleScope && role?.roleScopes?.includes(roleScope));
}
